// src/pages/repayments/Repayments.js
import React, { useState, useEffect } from "react";
import styled from "styled-components";
import { FiEye, FiDollarSign, FiMail, FiLock } from "react-icons/fi";
import { db } from "../firebase";
import { collection, getDocs, query, orderBy, doc, updateDoc, arrayUnion } from "firebase/firestore";
import toast from "react-hot-toast";
import { setLoading } from "../redux/loaderSlice";
import { useDispatch } from "react-redux";

/* ================= STYLES ================= */

const Wrapper = styled.div`
  padding: 20px;
  margin-top: 60px; /* To offset fixed header */
`;

/* ===== FILTER + SEARCH ===== */
const FilterSearch = styled.div`
  display: flex;
  gap: 16px;
  margin-bottom: 20px;
  flex-wrap: wrap;
`;

const Select = styled.select`
  padding: 8px 12px;
  border-radius: 6px;
  border: 1px solid #D1D5DB;
  font-size: 14px;
  background: #fff;
  color: #111827;

  &:focus {
    outline: none;
    border-color: #2563EB;
    box-shadow: 0 0 0 2px rgba(37, 99, 235, 0.15);
  }
`;

const SearchInput = styled.input`
  flex: 1;
  min-width: 180px;
  padding: 8px 12px;
  border-radius: 6px;
  border: 1px solid #D1D5DB;
  font-size: 14px;
  background: #fff;
  color: #111827;

  &:focus {
    outline: none;
    border-color: #2563EB;
    box-shadow: 0 0 0 2px rgba(37, 99, 235, 0.15);
  }
`;

/* ===== SUMMARY CARDS ===== */
const SummaryCards = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 16px;
  margin-bottom: 24px;

  @media (max-width: 1024px) {
    grid-template-columns: repeat(2, 1fr);
  }
`;

const Card = styled.div`
  background: #fff;
  padding: 16px;
  border-radius: 10px;
  box-shadow: 0 4px 12px rgba(0,0,0,0.04);
  border: 1px solid #f3f4f6;
  display: flex;
  flex-direction: column;
  justify-content: center;
`;

const CardTitle = styled.div`
  font-size: 12px;
  color: #6B7280;
`;

const CardValue = styled.div`
  font-size: 18px;
  font-weight: bold;
  margin-top: 6px;
`;

/* ===== TIMELINE CARDS ===== */
const Timeline = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const TimelineCard = styled.div`
  background: #fff;
  border-radius: 10px;
  padding: 16px;
  box-shadow: 0 6px 18px rgba(0,0,0,0.05);
  display: flex;
  flex-direction: column;

  @media (min-width: 768px) {
    flex-direction: row;
    justify-content: space-between;
    align-items: center;
  }
`;

const InfoBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  width: 100%;

  @media (min-width: 768px) {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 16px;
    align-items: center;
    flex: 1;
  }

  @media (min-width: 1100px) {
    display: flex;
    flex-direction: row;
    flex-wrap: nowrap;
    justify-content: space-between;
    gap: 12px;
  }
`;

const InfoItem = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-bottom: 1px solid #f3f4f6;
  padding-bottom: 6px;

  &:last-child {
    border-bottom: none;
    padding-bottom: 0;
  }

  @media (min-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 4px;
    border-bottom: none;
    padding-bottom: 0;
  }
`;

const Label = styled.span`
  font-size: 12px;
  color: #6B7280;
  font-weight: 500;
`;

const Value = styled.span`
  font-weight: 600;
  font-size: 13px;
  color: #111827;
  white-space: nowrap;
`;

const Status = styled.span`
  padding: 4px 10px;
  border-radius: 20px;
  font-size: 11px;
  font-weight: 600;
  text-transform: capitalize;

  ${({ type }) =>
    type === "active" && `background:#DCFCE7;color:#166534;`}
  ${({ type }) =>
    type === "closed" && `background:#E5E7EB;color:#374151;`}
  ${({ type }) =>
    type === "overdue" && `background:#FEE2E2;color:#991B1B;`}
`;

/* ===== ACTIONS ===== */
const Actions = styled.div`
  display: flex;
  gap: 8px;
  margin-top: 16px;
  padding-top: 12px;
  border-top: 1px solid #f3f4f6;

  @media (min-width: 768px) {
    margin-top: 0;
    padding-top: 0;
    border-top: none;
    margin-left: 20px;
    flex-shrink: 0;
  }
`;

const Btn = styled.button`
  flex: 1;
  min-height: 40px;
  font-size: 18px;
  border-radius: 8px;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: opacity 0.2s;

  &:hover {
    opacity: 0.85;
  }

  @media (min-width: 768px) {
    flex: none;
    min-width: 38px;
    min-height: 38px;
  }

  ${({ type }) =>
    type === "view" && `background:#DBEAFE;color:#1D4ED8;`}
  ${({ type }) =>
    type === "pay" && `background:#D1FAE5;color:#065F46;`}
  ${({ type }) =>
    type === "mail" && `background:#FEF3C7;color:#92400E;`}
  ${({ type }) =>
    type === "close" && `background:#FEE2E2;color:#991B1B;`}
`;

/* ===== ACTION MODALS STYLES ===== */
const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0,0,0,0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
  backdrop-filter: blur(2px);
`;

const ModalContent = styled.div`
  background: #fff;
  padding: 24px;
  border-radius: 12px;
  width: 100%;
  max-width: 500px;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1);
`;

const FormGroup = styled.div`
  margin-bottom: 12px;
  display: flex;
  flex-direction: column;
`;

const FormLabel = styled.label`
  font-size: 13px;
  color: #6b7280;
  margin-bottom: 4px;
`;

const FormInput = styled.input`
  padding: 8px 10px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  font-size: 14px;
  outline: none;
  &:focus {
    border-color: #f59e0b;
  }
`;

const ModalActions = styled.div`
  display: flex;
  gap: 10px;
  margin-top: 20px;
  justify-content: flex-end;
`;

const ModalBtn = styled.button`
  padding: 8px 16px;
  border-radius: 6px;
  border: none;
  cursor: pointer;
  font-weight: 500;
  background: ${({ primary }) => (primary ? "#f59e0b" : "#e5e7eb")};
  color: ${({ primary }) => (primary ? "#fff" : "#374151")};
  &:hover {
    opacity: 0.9;
  }
`;

/* ================= COMPONENT ================= */

export default function Repayments() {
  const dispatch = useDispatch();
  const [repayments, setRepayments] = useState([]);
  const [filter, setFilter] = useState("all");
  const [search, setSearch] = useState("");

  const [actionModal, setActionModal] = useState({ type: null, loan: null });
  const [payAmount, setPayAmount] = useState("");
  const [closeRemarks, setCloseRemarks] = useState("");

  const fetchLoans = async () => {
    dispatch(setLoading(true));
    try {
      const q = query(collection(db, "billing"), orderBy("createdAt", "desc"));
      const snap = await getDocs(q);

      const fetchedLoans = snap.docs.map((d) => {
        const data = d.data();
        const form = data.form || {};
        const ornaments = data.ornaments || [];

        const totalWeight = ornaments.reduce((sum, o) => sum + Number(o.net || 0), 0);
        const amount = Number(form.loanAmount) || 0;
        const interest = Number(form.interest) || 0;
        const totalWithInterest = amount + (amount * interest / 100);

        const outstanding = data.outstanding !== undefined ? Number(data.outstanding) : totalWithInterest;
        const paid = totalWithInterest - outstanding;

        const payments = data.payments || [];
        const closingPayment = payments.find(p => p.isClosing && p.remarks);

        return {
          dbId: d.id,
          loanId: data.customerId || d.id,
          name: form.customerName || "-",
          phone: form.phone || "-",
          weight: totalWeight,
          amount: amount,
          interest: interest,
          totalWithInterest: totalWithInterest,
          outstanding: outstanding,
          paid: paid > 0 ? paid : 0,
          startDate: form.startDate || "-",
          dueDate: form.endDate || "-",
          remarks: closingPayment ? closingPayment.remarks : "-",
        };
      });

      setRepayments(fetchedLoans);
    } catch (err) {
      console.error("Failed to fetch loans:", err);
      toast.error("Failed to fetch data");
    } finally {
      dispatch(setLoading(false));
      toast.dismiss();
    }
  };

  useEffect(() => {
    fetchLoans();
  }, []);

  const getStatus = (r) => {
    const today = new Date();
    const due = new Date(r.dueDate);
    const outstanding = r.outstanding;
    if (outstanding <= 0) return "closed";
    if (today > due) return "overdue";
    return "active";
  };

  const handleAction = (type, loan) => {
    setActionModal({ type, loan });
    if (type === "pay") {
      setPayAmount("");
      setCloseRemarks("");
    } else if (type === "close") {
      setCloseRemarks("");
    }
  };

  const submitPay = async () => {
    const loan = actionModal.loan;
    const payment = Number(payAmount) || 0;
    const outstanding = loan.outstanding;
    if (payment <= 0) return toast.error("Enter valid amount");
    if (payment > outstanding) return toast.error("Payment exceeds outstanding");

    try {
      dispatch(setLoading(true));
      const newOutstanding = outstanding - payment;
      const paymentRecord = {
        amount: payment,
        date: new Date().toISOString()
      };
      if (newOutstanding === 0) {
        paymentRecord.isClosing = true;
        paymentRecord.remarks = closeRemarks;
      }

      await updateDoc(doc(db, "billing", loan.dbId), {
        outstanding: newOutstanding,
        payments: arrayUnion(paymentRecord)
      });
      toast.dismiss();
      toast.success("Payment recorded");
      setActionModal({ type: null, loan: null });
      fetchLoans();
    } catch (err) {
      dispatch(setLoading(false));
      toast.error("Failed to process");
    } finally {
      dispatch(setLoading(false));
      toast.dismiss();
    }
  };

  const submitClose = async () => {
    const loan = actionModal.loan;
    try {
      dispatch(setLoading(true)); 
      await updateDoc(doc(db, "billing", loan.dbId), {
        outstanding: 0,
        payments: arrayUnion({
          amount: loan.outstanding,
          date: new Date().toISOString(),
          isClosing: true,
          remarks: closeRemarks
        })
      });
      toast.dismiss();
      toast.success("Loan closed");
      setActionModal({ type: null, loan: null });
      fetchLoans();
    } catch (err) {
      dispatch(setLoading(false));
      toast.dismiss();
      toast.error("Failed to close loan");
    } finally {
      dispatch(setLoading(false));
      toast.dismiss();
    }
  };

    // const handleMail = (loan) => {
    //   toast.success(`Mail triggered for ${loan.name}`);
    // };

  /* ===== SUMMARY ===== */
  const totalPayments = repayments.reduce((sum, r) => sum + r.paid, 0);
  const totalOutstanding = repayments.reduce((sum, r) => sum + r.outstanding, 0);
  const upcomingPayments = repayments.filter(r => getStatus(r) === "active").length;
  const avgInterest = repayments.length
    ? (repayments.reduce((sum, r) => sum + r.interest, 0) / repayments.length).toFixed(2)
    : 0;

  /* ===== FILTER + SEARCH ===== */
  const filtered = repayments.filter(r => {
    const status = getStatus(r);
    if (filter !== "all" && status !== filter) return false;
    if (!r.name.toLowerCase().includes(search.toLowerCase()) &&
      !r.loanId.toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  });

  return (
    <Wrapper>
      {/* ===== FILTER + SEARCH ===== */}
      <FilterSearch>
        <Select value={filter} onChange={e => setFilter(e.target.value)}>
          <option value="all">All Status</option>
          <option value="active">Active</option>
          <option value="overdue">Overdue</option>
          <option value="closed">Closed</option>
        </Select>
        <SearchInput
          placeholder="Search by customer or loan ID"
          value={search}
          onChange={e => setSearch(e.target.value)}
        />
      </FilterSearch>

      {/* ===== SUMMARY CARDS ===== */}
      <SummaryCards>
        <Card><CardTitle>Total Payments</CardTitle><CardValue>₹ {totalPayments}</CardValue></Card>
        <Card><CardTitle>Total Outstanding</CardTitle><CardValue>₹ {totalOutstanding}</CardValue></Card>
        <Card><CardTitle>Average Interest</CardTitle><CardValue>{avgInterest}%</CardValue></Card>
        <Card><CardTitle>Upcoming Payments</CardTitle><CardValue>{upcomingPayments}</CardValue></Card>
      </SummaryCards>

      {/* ===== TIMELINE CARDS ===== */}
      <Timeline>
        {filtered.map((r, i) => {
          const status = getStatus(r);
          const outstanding = r.outstanding;

          return (
            <TimelineCard key={i}>
              <InfoBlock>
                <InfoItem>
                  <Label>Loan ID</Label> <Value>{r.loanId}</Value>
                </InfoItem>
                <InfoItem>
                  <Label>Name</Label> <Value>{r.name}</Value>
                </InfoItem>
                <InfoItem>
                  <Label>Phone</Label> <Value>{r.phone}</Value>
                </InfoItem>
                <InfoItem>
                  <Label>Loan</Label> <Value>₹ {r.amount}</Value>
                </InfoItem>
                <InfoItem>
                  <Label>Paid</Label> <Value>₹ {r.paid}</Value>
                </InfoItem>
                <InfoItem>
                  <Label>Outstanding</Label> <Value>₹ {outstanding}</Value>
                </InfoItem>
                <InfoItem>
                  <Label>Due Date</Label> <Value>{r.dueDate}</Value>
                </InfoItem>
                <InfoItem>
                  <Label>Status</Label> <Status type={status}>{status}</Status>
                </InfoItem>
              </InfoBlock>

              <Actions>
                <Btn type="view" onClick={() => handleAction("view", r)}><FiEye /></Btn>
                <Btn type="pay" onClick={() => handleAction("pay", r)}><FiDollarSign /></Btn>
                {/* <Btn type="mail" onClick={() => handleMail(r)}><FiMail /></Btn> */}
                <Btn type="close" onClick={() => handleAction("close", r)}><FiLock /></Btn>
              </Actions>
            </TimelineCard>
          );
        })}
      </Timeline>
      {/* ===== ACTION MODALS ===== */}
      {
        actionModal.type === "view" && (
          <ModalOverlay onClick={() => setActionModal({ type: null, loan: null })}>
            <ModalContent onClick={(e) => e.stopPropagation()}>
              <h3 style={{ marginTop: 0, borderBottom: "1px solid #eee", paddingBottom: 10 }}>View Loan Details</h3>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
                <FormGroup><FormLabel>Loan ID</FormLabel><div style={{ fontWeight: 600 }}>{actionModal.loan.loanId}</div></FormGroup>
                <FormGroup><FormLabel>Customer Name</FormLabel><div style={{ fontWeight: 600 }}>{actionModal.loan.name}</div></FormGroup>
                <FormGroup><FormLabel>Phone</FormLabel><div style={{ fontWeight: 600 }}>{actionModal.loan.phone}</div></FormGroup>
                <FormGroup><FormLabel>Loan Amount</FormLabel><div style={{ fontWeight: 600 }}>₹{actionModal.loan.amount}</div></FormGroup>
                <FormGroup><FormLabel>Total Amount with Interest</FormLabel><div style={{ fontWeight: 600 }}>₹{actionModal.loan.totalWithInterest}</div></FormGroup>
                <FormGroup><FormLabel>Outstanding</FormLabel><div style={{ fontWeight: 600, color: "#991b1b" }}>₹{actionModal.loan.outstanding}</div></FormGroup>
                <FormGroup><FormLabel>Amount Paid Till Date</FormLabel><div style={{ fontWeight: 600 }}>₹{actionModal.loan.paid}</div></FormGroup>
                <FormGroup><FormLabel>Interest</FormLabel><div style={{ fontWeight: 600 }}>{actionModal.loan.interest}%</div></FormGroup>
                <FormGroup><FormLabel>Start Date</FormLabel><div style={{ fontWeight: 600 }}>{actionModal.loan.startDate}</div></FormGroup>
                <FormGroup><FormLabel>Due Date</FormLabel><div style={{ fontWeight: 600 }}>{actionModal.loan.dueDate}</div></FormGroup>
                <FormGroup><FormLabel>Total Gold (Net)</FormLabel><div style={{ fontWeight: 600 }}>{actionModal.loan.weight} g</div></FormGroup>
                <FormGroup><FormLabel>Status</FormLabel><div style={{ fontWeight: 600, textTransform: "capitalize" }}>{getStatus(actionModal.loan)}</div></FormGroup>
                {actionModal.loan.remarks && actionModal.loan.remarks !== "-" && (
                  <FormGroup style={{ gridColumn: "1 / -1" }}>
                    <FormLabel>Closing Remarks</FormLabel>
                    <div style={{ fontWeight: 600 }}>{actionModal.loan.remarks}</div>
                  </FormGroup>
                )}
              </div>
              <ModalActions>
                <ModalBtn onClick={() => setActionModal({ type: null, loan: null })}>Close</ModalBtn>
              </ModalActions>
            </ModalContent>
          </ModalOverlay>
        )
      }

      {
        actionModal.type === "pay" && (
          <ModalOverlay onClick={() => setActionModal({ type: null, loan: null })}>
            <ModalContent onClick={(e) => e.stopPropagation()}>
              <h3 style={{ marginTop: 0, borderBottom: "1px solid #eee", paddingBottom: 10 }}>Process Payment</h3>
              <div style={{ marginBottom: 16 }}>
                <div style={{ fontSize: 13, color: "#6b7280", marginBottom: 4 }}>Current Outstanding</div>
                <div style={{ fontSize: 24, fontWeight: "bold", color: "#991b1b" }}>₹{actionModal.loan.outstanding}</div>
              </div>
              <FormGroup>
                <FormLabel>Amount to Pay</FormLabel>
                <FormInput
                  autoFocus
                  placeholder="Enter amount"
                  value={payAmount}
                  onChange={(e) => setPayAmount(e.target.value.replace(/\D/g, ""))}
                />
              </FormGroup>
              {Number(payAmount) === actionModal.loan.outstanding && (
                <FormGroup>
                  <FormLabel>Closing Remarks (Optional)</FormLabel>
                  <FormInput
                    placeholder="Enter remarks..."
                    value={closeRemarks}
                    onChange={(e) => setCloseRemarks(e.target.value)}
                  />
                </FormGroup>
              )}
              <ModalActions>
                <ModalBtn onClick={() => setActionModal({ type: null, loan: null })}>Cancel</ModalBtn>
                <ModalBtn primary onClick={submitPay}>Confirm Payment</ModalBtn>
              </ModalActions>
            </ModalContent>
          </ModalOverlay>
        )
      }

      {
        actionModal.type === "close" && (
          <ModalOverlay onClick={() => setActionModal({ type: null, loan: null })}>
            <ModalContent onClick={(e) => e.stopPropagation()}>
              <h3 style={{ marginTop: 0, borderBottom: "1px solid #eee", paddingBottom: 10 }}>Close Loan</h3>
              <div style={{ marginBottom: 16 }}>
                <div style={{ fontSize: 13, color: "#6b7280", marginBottom: 4 }}>Outstanding to be waived/marked closed</div>
                <div style={{ fontSize: 24, fontWeight: "bold", color: "#991b1b" }}>₹{actionModal.loan.outstanding}</div>
              </div>
              <FormGroup>
                <FormLabel>Closing Remarks</FormLabel>
                <FormInput
                  autoFocus
                  placeholder="Enter remarks..."
                  value={closeRemarks}
                  onChange={(e) => setCloseRemarks(e.target.value)}
                />
              </FormGroup>
              <ModalActions>
                <ModalBtn onClick={() => setActionModal({ type: null, loan: null })}>Cancel</ModalBtn>
                <ModalBtn primary onClick={submitClose}>Close Loan</ModalBtn>
              </ModalActions>
            </ModalContent>
          </ModalOverlay>
        )
      }
    </Wrapper>
  );
}